/* One-time annual and single-item options alongside Ka-Ching. Shopify enforces discounts. */
(() => {
  'use strict';
  if (customElements.get('sebi-annual-supply')) return;

  const QUANTITY = 12;
  const KEEP_PERCENT = 60;
  const OTHER_OPTIONS = 'kaching-bundles-block, [data-bundle-widget], .subw';
  const EXPRESS_CHECKOUT = '.shopify-payment-button, shopify-accelerated-checkout';
  const setText = (element, text) => {
    if (element && element.textContent !== text) element.textContent = text;
  };
  const validId = (id) => /^\d+$/.test(String(id || ''));
  const totalPrice = (price) => Math.round(Number(price) * QUANTITY * KEEP_PERCENT / 100);

  class AnnualSupply extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const configElement = this.querySelector('[data-annual-config]');
      const required = ['[data-annual-select]', '[data-annual-status]', '[data-annual-error]', '[data-annual-cart]'];
      let config;
      try {
        config = JSON.parse(configElement?.textContent || '');
      } catch (_) { /* Shopify can connect the element before its children arrive. */ }
      if (!config || !Array.isArray(config.variants) || !Array.isArray(config.gifts) ||
          required.some((selector) => !this.querySelector(selector)) ||
          (config.singleEnabled && !this.querySelector('[data-single-select]'))) {
        this.waitForMarkup();
        return;
      }
      this.stopWaitingForMarkup();
      try {
        this.initialize(config);
        this.dataset.annualState = 'ready';
      } catch (error) {
        this.active = false;
        this.mode = null;
        this.controller?.abort();
        this.controller = null;
        this.observer?.disconnect();
        this.restorePurchaseControls();
        this.restoreKachingSelection();
        this.showLoadingError();
        console.warn('[Annual supply] Initialization failed.', error);
      }
    }

    waitForMarkup() {
      this.querySelectorAll('[data-annual-select], [data-single-select]').forEach((select) => { select.disabled = true; });
      if (!this.markupObserver) {
        this.markupObserver = new MutationObserver(() => {
          if (this.isConnected) this.connectedCallback();
        });
        this.markupObserver.observe(this, { childList: true, subtree: true, characterData: true });
      }
      if (!this.markupTimer) {
        this.markupTimer = setTimeout(() => {
          if (this.isConnected && !this.controller) this.showLoadingError();
        }, 1500);
      }
    }

    stopWaitingForMarkup() {
      this.markupObserver?.disconnect();
      this.markupObserver = null;
      clearTimeout(this.markupTimer);
      this.markupTimer = null;
    }

    showLoadingError() {
      this.dataset.annualState = 'load-error';
      this.querySelectorAll('[data-annual-select], [data-single-select]').forEach((select) => { select.checked = false; select.disabled = true; });
      this.classList.remove('is-selected', 'is-single-selected');
      const status = this.querySelector('[data-annual-status]');
      status?.classList.add('annual-supply__status--visible');
      setText(status, 'This option could not load. Please refresh the page.');
      // Liquid only emits enabled offers or an editor preview. A failed startup
      // must stay non-purchasable, but should explain itself instead of vanishing.
      if (this.dataset.enabled === 'true' || this.dataset.singleEnabled === 'true' || window.Shopify?.designMode || this.config?.designMode) this.hidden = false;
    }

    initialize(config) {
      this.config = config;
      this.controller = new AbortController();
      this.suppressed = new Map();
      this.quantityState = new Map();
      this.kachingSelections = new Map();
      this.singleFormState = new Map();
      this.singleFormClasses = new Map();
      this.annualCheckoutFields = new Map();
      this.active = false;
      this.mode = null;
      this.busy = this.busy || false;
      this.locked = this.locked || false;
      this.scope = this.closest('.product__info-container') || this.closest('.shopify-section') || this.parentElement;
      this.form = document.getElementById(this.dataset.formId);
      this.select = this.querySelector('[data-annual-select]');
      this.singleSelect = this.querySelector('[data-single-select]');
      this.addButton = this.form?.querySelector('[name="add"][type="submit"], button[type="submit"], input[type="submit"]');
      this.status = this.querySelector('[data-annual-status]');
      this.error = this.querySelector('[data-annual-error]');
      this.cartLink = this.querySelector('[data-annual-cart]');
      this.syncSelection();
      const opts = { signal: this.controller.signal };
      this.select.addEventListener('change', () => this.setActive(this.select.checked), opts);
      this.singleSelect?.addEventListener('change', () => this.setChoice(this.singleSelect.checked ? 'single' : null), opts);
      // Capture before theme and app handlers. The original button and its label stay intact.
      window.addEventListener('click', (event) => {
        if (!this.active) return;
        if (event.composedPath().includes(this.addButton)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.addToCart();
        } else if (this.isOtherOption(event.target)) {
          if (this.busy) { event.preventDefault(); event.stopImmediatePropagation(); return; }
          // Let the clicked app row become selected. Restoring it first would
          // make Ka-Ching treat a click on the previous row as a deselection.
          this.setActive(false, false);
        }
      }, { ...opts, capture: true });
      window.addEventListener('change', (event) => {
        if (this.active && this.isOtherOption(event.target)) this.setActive(false, false);
      }, { ...opts, capture: true });
      this.scope.addEventListener('change', (event) => {
        if (!this.contains(event.target)) queueMicrotask(() => this.refresh());
      }, opts);
      // Capture also observes documented Ka-Ching events that do not bubble.
      this.scope.addEventListener('deal-bar-selected', (event) => {
        if (this.syncingKaching) return;
        // A late app mount must not replace the shopper's explicit annual choice.
        if (event.detail?.preselected) {
          queueMicrotask(() => this.updatePurchaseControls());
          return;
        }
        this.setActive(false, false);
      }, { ...opts, capture: true });
      for (const eventName of ['variant-selected', 'variants-changed']) {
        this.scope.addEventListener(eventName, () => queueMicrotask(() => this.refresh()), { ...opts, capture: true });
      }
      for (const eventName of ['items-changed', 'kaching-bundles-block-loaded']) {
        this.scope.addEventListener(eventName, () => queueMicrotask(() => this.updatePurchaseControls()), { ...opts, capture: true });
      }
      window.addEventListener('submit', (event) => {
        if (!this.active || event.target !== this.form) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.addToCart();
      }, { ...opts, capture: true });
      // App widgets and purchase controls can mount late or be replaced in the editor.
      this.observer = new MutationObserver(() => {
        const form = document.getElementById(this.dataset.formId);
        const button = form?.querySelector('[name="add"][type="submit"], button[type="submit"], input[type="submit"]');
        if (button !== this.addButton || form !== this.form) {
          this.restorePurchaseControls();
          this.form = form;
          this.addButton = button;
          if (this.active && button) this.claimSharedButton();
          this.refresh();
        }
        this.placeAbovePurchaseControls();
        if (this.active) this.updatePurchaseControls();
      });
      this.observer.observe(this.scope, { childList: true, subtree: true, attributes: true, attributeFilter: ['name', 'value'] });
      this.placeAbovePurchaseControls();
      this.refresh();
    }

    disconnectedCallback() {
      // Moving this same element within the product section must preserve selection
      // and an in-flight cart request. A real removal still performs normal cleanup.
      if (this.relocating) return;
      this.stopWaitingForMarkup();
      this.active = false;
      this.mode = null;
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.restorePurchaseControls();
      this.restoreKachingSelection();
    }

    placeAbovePurchaseControls() {
      if (!this.isConnected || !this.form || !this.addButton || !this.scope.contains(this.form)) return;
      const toolbar = this.addButton.closest('.product-form__buttons');
      // Stay outside the horizontal button row and the app's own rendered subtree.
      // Ka-Ching can insert its options before this row after the initial page load.
      const anchor = toolbar && this.form.contains(toolbar) ? toolbar : this.form;
      const parent = anchor.parentElement;
      if (!parent || this.contains(anchor) || parent.closest(OTHER_OPTIONS) ||
          (this.parentElement === parent && this.nextElementSibling === anchor)) return;
      const focused = this.contains(document.activeElement) ? document.activeElement : null;
      this.relocating = true;
      try {
        parent.insertBefore(this, anchor);
      } finally {
        this.relocating = false;
      }
      focused?.focus({ preventScroll: true });
    }

    isOtherOption(target) {
      const option = target instanceof Element ? target.closest(OTHER_OPTIONS) : null;
      return Boolean(option && this.scope.contains(option) && !option.contains(this));
    }

    suspendKachingSelection() {
      if (!this.active || this.syncingKaching) return;
      this.syncingKaching = true;
      try {
        this.scope.querySelectorAll('kaching-bundles-block').forEach((host) => {
          // This is an exposed property in the store's Ka-Ching component. Wait
          // for upgrade/loading instead of creating a property on an empty host.
          if (!('selectedDealBarId' in host) || !host.selectedDealBarId) return;
          if (!this.kachingSelections.has(host)) this.kachingSelections.set(host, host.selectedDealBarId);
          host.selectedDealBarId = undefined;
        });
      } finally {
        this.syncingKaching = false;
      }
    }

    restoreKachingSelection() {
      this.syncingKaching = true;
      try {
        this.kachingSelections?.forEach((selectedId, host) => {
          // A click/change in Ka-Ching may already have chosen a different deal.
          // Never overwrite that choice, or a replacement widget's configuration.
          if (!host.isConnected || !this.scope.contains(host) || host.selectedDealBarId) return;
          const bars = host.dealBlock?.dealBars;
          if (Array.isArray(bars) && !bars.some((bar) => bar.id === selectedId)) return;
          host.selectedDealBarId = selectedId;
        });
      } finally {
        this.kachingSelections?.clear();
        this.syncingKaching = false;
      }
    }

    giftConfigurationValid() {
      const gifts = this.config.gifts;
      return Array.isArray(gifts) && gifts.length === 3 &&
        gifts.every((gift) => gift && validId(gift.id) && validId(gift.productId) &&
          String(gift.productId) !== String(this.config.productId)) &&
        new Set(gifts.map((gift) => String(gift.productId))).size === 3;
    }

    selectedVariant() {
      const variants = this.config.variants || [];
      const picker = this.scope.querySelector('variant-radios, variant-selects');
      if (picker) {
        const values = picker.matches('variant-radios')
          ? [...picker.querySelectorAll('fieldset')].map((field) => field.querySelector('input:checked')?.value)
          : [...picker.querySelectorAll('select')].map((select) => select.value);
        if (values.length) {
          return variants.find((variant) => variant.options?.length === values.length &&
            variant.options.every((value, index) => value === values[index]));
        }
      }
      const id = this.form?.querySelector('[name="id"]')?.value || this.config.selectedVariantId;
      return variants.find((variant) => String(variant.id) === String(id));
    }

    supportsQuantity(variant, quantity) {
      if (!variant || !variant.available || variant.requiresSellingPlan) return false;
      const rule = variant.quantityRule || {};
      const min = Number(rule.min || 1);
      const increment = Number(rule.increment || 1);
      if (quantity < min || (rule.max && quantity > Number(rule.max)) || quantity % increment !== 0) return false;
      return !(variant.inventoryManagement && variant.inventoryPolicy === 'deny' &&
        Number(variant.inventoryQuantity) < quantity);
    }

    unavailableReason(mode = this.mode || 'annual') {
      if (mode === 'single') {
        if (!this.config.singleEnabled || !this.singleSelect) return 'This option is unavailable.';
      } else if (!this.config.enabled || !this.giftConfigurationValid()) return 'This offer is awaiting setup.';
      if (!this.form || !this.addButton) return 'This offer needs the product Add to cart button.';
      if (this.config.requiresSellingPlan) return 'This product is available by subscription only.';
      if (mode === 'single') return this.supportsQuantity(this.selectedVariant(), 1) ? '' : 'One item is unavailable for this option.';
      if (!this.supportsQuantity(this.selectedVariant(), QUANTITY)) return 'A 12-month supply is unavailable for this option.';
      return '';
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(this.config.locale || 'en', {
          style: 'currency', currency: this.config.currency || 'USD'
        }).format(cents / 100);
      } catch (_) {
        return `${(cents / 100).toFixed(2)} ${this.config.currency || ''}`.trim();
      }
    }

    refresh() {
      if (!this.isConnected || this.busy) return;
      const ready = this.config.enabled && this.giftConfigurationValid();
      const singleReady = Boolean(this.config.singleEnabled && this.singleSelect && !this.config.requiresSellingPlan);
      const annualOffer = this.querySelector('[data-annual-offer]');
      if (annualOffer) annualOffer.hidden = !ready && !this.config.designMode;
      if (this.singleSelect) this.singleSelect.closest('.annual-supply__card').hidden = !singleReady;
      this.hidden = !ready && !singleReady && !this.config.designMode;
      const variant = this.selectedVariant();
      setText(this.querySelector('[data-annual-price]'), variant ? this.money(totalPrice(variant.price)) : 'Unavailable');
      setText(this.querySelector('[data-annual-compare]'), variant ? this.money(Number(variant.price) * QUANTITY) : '');
      setText(this.querySelector('[data-single-price]'), variant ? this.money(Number(variant.price)) : 'Unavailable');
      const reason = this.unavailableReason(this.mode || (!ready && singleReady && !this.config.designMode ? 'single' : 'annual'));
      // Omit the editor setup message without changing availability checks or cart errors.
      const displayReason = reason === 'This offer is awaiting setup.' ? '' : reason;
      this.status.classList.toggle('annual-supply__status--visible', Boolean(displayReason));
      this.updateChoiceAvailability();
      this.dataset.available = String(!reason);
      if (reason && this.active && !this.locked) this.setActive(false);
      if (this.active) this.updatePurchaseControls();
      if (!this.locked) setText(this.status, this.hidden ? '' : displayReason);
    }

    setActive(active, restoreKaching = true) {
      this.setChoice(active ? 'annual' : null, restoreKaching);
    }

    syncSelection() {
      this.select.checked = this.mode === 'annual';
      if (this.singleSelect) this.singleSelect.checked = this.mode === 'single';
      this.classList.toggle('is-selected', this.mode === 'annual');
      this.classList.toggle('is-single-selected', this.mode === 'single');
    }

    updateChoiceAvailability() {
      this.select.disabled = this.busy || Boolean(this.unavailableReason('annual')) || (this.locked && this.mode !== 'annual');
      if (this.singleSelect) this.singleSelect.disabled = this.busy || Boolean(this.unavailableReason('single')) || (this.locked && this.mode !== 'single');
    }

    setChoice(mode, restoreKaching = true) {
      if (this.busy) { this.syncSelection(); return; }
      // The shared button may already belong to an in-flight regular purchase.
      if (mode && !this.active && (this.addButton?.classList.contains('loading') || this.addButton?.getAttribute('aria-busy') === 'true')) {
        this.syncSelection();
        this.status.classList.add('annual-supply__status--visible');
        setText(this.status, 'Please wait for your current cart update to finish.');
        return;
      }
      if (mode && (this.unavailableReason(mode) || this.locked)) {
        this.syncSelection();
        return;
      }
      // Both custom options are one-time purchases. Keep the app fields suspended
      // across annual/single switches; restore them when leaving both options.
      this.mode = mode;
      this.active = Boolean(mode);
      this.syncSelection();
      if (this.active) {
        this.status.classList.remove('annual-supply__status--visible');
        this.claimSharedButton();
        this.updatePurchaseControls();
        setText(this.status, mode === 'single' ? 'One-time purchase selected. Add to cart will include one item.' : '12-month supply selected. Add to cart will include 12 units.');
      } else {
        this.restorePurchaseControls();
        if (restoreKaching) this.restoreKachingSelection();
        else this.kachingSelections.clear();
        if (!this.locked) setText(this.status, '');
      }
      this.updateChoiceAvailability();
    }

    claimSharedButton() {
      if (!this.addButton || this.buttonState) return;
      const button = this.addButton;
      const spinner = button.querySelector('.loading-overlay__spinner');
      this.buttonState = {
        button, disabled: button.disabled, ariaDisabled: button.getAttribute('aria-disabled'),
        ariaBusy: button.getAttribute('aria-busy'), loading: button.classList.contains('loading'),
        spinner, spinnerHidden: spinner?.classList.contains('hidden')
      };
    }

    updatePurchaseControls() {
      if (!this.active || !this.form || !this.addButton) return;
      if (!this.busy && !this.locked) this.restoreExpressCheckout();
      else this.form.querySelectorAll(EXPRESS_CHECKOUT).forEach((element) => {
        if (element.contains(this.addButton) || this.suppressed.has(element)) return;
        this.suppressed.set(element, element.inert);
        element.classList.add('annual-supply-suppressed');
        element.inert = true;
      });
      // Associated quantity inputs can live outside the form. Do not dispatch app change events.
      // Templates without a quantity selector still need an explicit quantity for Shop Pay.
      const quantityInputs = [...this.form.elements].filter((control) => control.name === 'quantity' && !control.disabled);
      if (!quantityInputs.length) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        this.checkoutQuantityInput = input;
        this.form.append(input);
        quantityInputs.push(input);
      }
      quantityInputs.forEach((input) => {
        if (!this.quantityState.has(input)) {
          this.quantityState.set(input, { value: input.value, readOnly: input.readOnly });
          input.readOnly = true;
        }
        const quantity = this.mode === 'single' ? '1' : String(QUANTITY);
        if (input.value !== quantity) input.value = quantity;
        input.closest('quantity-input')?.querySelectorAll('button').forEach((button) => {
          if (!this.quantityState.has(button)) {
            this.quantityState.set(button, { disabled: button.disabled });
            button.disabled = true;
          }
        });
      });
      const blocked = this.busy || this.locked || Boolean(this.unavailableReason());
      if (this.addButton.disabled !== blocked) this.addButton.disabled = blocked;
      const ariaDisabled = String(blocked);
      if (this.addButton.getAttribute('aria-disabled') !== ariaDisabled) this.addButton.setAttribute('aria-disabled', ariaDisabled);
      if (this.busy) this.addButton.setAttribute('aria-busy', 'true');
      else this.addButton.removeAttribute('aria-busy');
      this.addButton.classList.toggle('loading', this.busy);
      this.buttonState?.spinner?.classList.toggle('hidden', !this.busy);
      this.suspendKachingSelection();
      // Native Shop Pay must use the same one-time purchase as our Add to cart payload.
      this.clearSinglePurchaseFields();
      this.syncAnnualCheckoutGifts();
    }

    syncAnnualCheckoutGifts() {
      // Ka-Ching Cart manages gifts. Do not add gift products to native Shop Pay.
      this.clearAnnualCheckoutGifts();
    }

    clearAnnualCheckoutGifts() {
      this.annualCheckoutFields?.forEach((input) => input.remove());
      this.annualCheckoutFields?.clear();
    }

    restoreExpressCheckout() {
      this.suppressed?.forEach((inert, element) => {
        element.classList.remove('annual-supply-suppressed');
        element.inert = inert;
      });
      this.suppressed?.clear();
    }

    clearSinglePurchaseFields() {
      // Keep Ka-Ching's sellingPlanId intact: it prices the visible subscription
      // options. Only the submitted form/payload should become a one-time purchase.
      // Accelerated checkout can read disabled inputs. Remove the names of
      // subscription/bundle fields reversibly, including app-injected extra gifts.
      [...this.form.elements].forEach((control) => {
        if (control.hasAttribute('data-sebi-annual-checkout-gift')) return;
        const name = control.getAttribute('name');
        if (!name || !(name === 'selling_plan' || name.startsWith('shopify-additional-items[') ||
            /^properties\[_{1,2}kaching/.test(name) || control.hasAttribute('data-kaching-bundles-additional-item'))) return;
        if (!this.singleFormState.has(control)) this.singleFormState.set(control, { name, value: control.value });
        control.removeAttribute('name');
        // Ka-Ching also watches the input's value setter. Removing its name is
        // enough to exclude it from checkout without repricing the other cards.
      });
      const hiddenClass = 'kaching-bundles-form--different-variants-selected';
      if (this.form.classList.contains(hiddenClass)) {
        this.singleFormClasses.set(this.form, true);
        this.form.classList.remove(hiddenClass);
      }
    }

    restoreSinglePurchaseFields() {
      // Ka-Ching can create replacements when a field temporarily has no name.
      // Restore one current field per name, not obsolete copies of an old deal.
      const latest = new Map();
      this.singleFormState?.forEach((state, control) => {
        if (!control.isConnected || control.hasAttribute('name')) return;
        if (control.type === 'hidden') latest.set(state.name, { control, state });
        else { control.value = state.value; control.setAttribute('name', state.name); }
      });
      latest.forEach(({ control, state }, name) => {
        const existing = [...(control.form?.elements || this.form?.elements || [])].find((field) => field.name === name);
        if (!existing) { control.value = state.value; control.setAttribute('name', name); }
      });
      this.singleFormState?.forEach((_, control) => {
        if (control.isConnected && control.type === 'hidden' && !control.hasAttribute('name')) control.remove();
      });
      this.singleFormState?.clear();
      this.singleFormClasses?.forEach((_, form) => form.classList.add('kaching-bundles-form--different-variants-selected'));
      this.singleFormClasses?.clear();
    }

    restorePurchaseControls() {
      this.restoreExpressCheckout();
      // Remove our checkout fields before restoring any app fields with the same names.
      this.clearAnnualCheckoutGifts();
      this.restoreSinglePurchaseFields();
      this.checkoutQuantityInput?.remove();
      this.checkoutQuantityInput = null;
      this.quantityState?.forEach((state, control) => {
        if ('value' in state) { control.value = state.value; control.readOnly = state.readOnly; }
        else control.disabled = state.disabled;
      });
      this.quantityState?.clear();
      const state = this.buttonState;
      if (state) {
        state.button.disabled = state.disabled;
        for (const [attribute, value] of [['aria-disabled', state.ariaDisabled], ['aria-busy', state.ariaBusy]]) {
          if (value === null) state.button.removeAttribute(attribute);
          else state.button.setAttribute(attribute, value);
        }
        state.button.classList.toggle('loading', state.loading);
        state.spinner?.classList.toggle('hidden', state.spinnerHidden);
        // A variant change can make the regular product unavailable during annual selection.
        const variant = this.selectedVariant();
        if (variant?.available === false || (!variant && this.scope.querySelector('variant-radios, variant-selects'))) {
          state.button.disabled = true;
          state.button.setAttribute('aria-disabled', 'true');
        }
      }
      this.buttonState = null;
    }

    cartUrl(path) {
      const root = window.Shopify?.routes?.root || '/';
      return `${root.endsWith('/') ? root : `${root}/`}${path}`;
    }

    buildItems(variant, bundleId, mode = this.mode || 'annual') {
      if (mode === 'single') return [{
        id: String(variant.id), quantity: 1,
        properties: { _sebi_single_purchase: bundleId }
      }];
      const properties = {
        _sebi_annual_bundle: bundleId,
        _sebi_annual_product: String(this.config.productId)
      };
      // A fresh payload intentionally contains no selling_plan, form quantity or app properties.
      // Ka-Ching Cart manages gifts; this widget adds only the selected supply.
      return [{
        id: String(variant.id), quantity: QUANTITY,
        properties: { ...properties, _sebi_annual_role: 'supply', Supply: '12 months (one-time)' }
      }];
    }

    verifiedSingleCart(cart, variant, bundleId) {
      if (!cart || cart.currency !== this.config.currency || !Array.isArray(cart.items)) return false;
      const lines = cart.items.filter((item) => item.properties?._sebi_single_purchase === bundleId);
      return lines.length === 1 && String(lines[0].variant_id) === String(variant.id) &&
        lines[0].quantity === 1 && !lines[0].selling_plan_allocation;
    }

    refreshCartCount(result, cart) {
      const icon = document.getElementById('cart-icon-bubble');
      if (!icon) return;
      const html = result.sections?.['cart-icon-bubble'];
      if (typeof html === 'string' && html.trim()) {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const section = parsed.getElementById('shopify-section-cart-icon-bubble') || parsed.body;
        icon.innerHTML = section.innerHTML;
        return;
      }
      // A section rendering failure must not turn a successful add into an error.
      if (!Number.isFinite(cart.item_count)) return;
      let bubble = icon.querySelector('.cart-count-bubble');
      if (!cart.item_count) { bubble?.remove(); return; }
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        icon.append(bubble);
      }
      const count = document.createElement('span');
      count.setAttribute('aria-hidden', 'true');
      count.textContent = cart.item_count < 100 ? String(cart.item_count) : '';
      const label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'} in cart`;
      bubble.replaceChildren(count, label);
    }

    async addToCart() {
      if (!this.active || this.busy || this.locked) return;
      const reason = this.unavailableReason();
      if (reason) { this.refresh(); return; }
      const variant = this.selectedVariant();
      const mode = this.mode;
      const single = mode === 'single';
      const bundleId = window.crypto?.randomUUID?.() || `${single ? 'single' : 'annual'}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      this.busy = true;
      this.locked = true; // Never retry a mutation with an uncertain outcome automatically.
      this.setAttribute('aria-busy', 'true');
      this.updateChoiceAvailability();
      this.updatePurchaseControls();
      this.error.hidden = true;
      this.cartLink.hidden = true;
      setText(this.status, single ? 'Adding one item…' : 'Adding your 12-month supply…');
      let added = false;
      try {
        // Ka-Ching uses this request-specific marker for already-built items.
        // Its current interceptor otherwise can reuse a recent subscription add.
        // Keep our explicit one-time product payload intact; no global toggle.
        const response = await fetch(this.cartUrl('cart/add.js?kaching_bundles=true'), {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: this.buildItems(variant, bundleId, mode),
            sections: ['cart-icon-bubble'], sections_url: window.location.pathname
          })
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(typeof result.description === 'string' ? result.description : 'The offer could not be added.');
        }
        added = true;
        let cart = null;
        if (single) {
          const cartResponse = await fetch(this.cartUrl('cart.js'), {
            credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }
          });
          if (!cartResponse.ok) throw new Error('Your items were added, but the cart could not be checked.');
          cart = await cartResponse.json();
          if (!this.verifiedSingleCart(cart, variant, bundleId)) {
            throw new Error('Your item was added, but the one-time quantity could not be confirmed. Please review your cart before checkout.');
          }
        } else if (!result.sections?.['cart-icon-bubble']) {
          // A cart-count refresh is optional after a successful annual add.
          // Do not block the shopper with the removed offer-verification messages.
          try {
            const cartResponse = await fetch(this.cartUrl('cart.js'), {
              credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }
            });
            if (cartResponse.ok) cart = await cartResponse.json();
          } catch (error) { console.warn('[Annual supply] Cart count could not refresh.', error); }
        }
        this.locked = false;
        try { this.refreshCartCount(result, cart || {}); }
        catch (error) { console.warn('[Product options] Cart count could not refresh.', error); }
        setText(this.status, 'Added to cart.');
        this.status.classList.add('annual-supply__status--visible');
        this.cartLink.href = this.cartUrl('cart');
        setText(this.cartLink, 'View cart');
        this.cartLink.hidden = false;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'The cart could not be updated.';
        setText(this.error, added ? message : `${message} Your cart may have changed. Review it before adding this offer again.`);
        this.error.hidden = false;
        setText(this.status, 'Please review your cart.');
        this.cartLink.href = this.cartUrl('cart');
        setText(this.cartLink, 'Review your cart');
        this.cartLink.hidden = false;
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
        this.updateChoiceAvailability();
        if (this.active) this.updatePurchaseControls();
        // Failed or uncertain requests remain locked; successful adds can be repeated deliberately.
      }
    }
  }

  customElements.define('sebi-annual-supply', AnnualSupply);
})();
